import type { ReactNode } from "react";

import ColoredLogo from "@/components/ui/RecolorableLogo";
import { workplaceToBrandSlug } from "@/lib/brand-logos/workplace-to-brand-slug";
import { formatImageUrl } from "@/lib/utils";
import { capitalizeCarBrand } from "@/utils/car-capitalize";

interface BrandExpertiseBadgeProps {
  workplace: {
    name: string;
    slug: string;
    image?: string | null;
    imageUrl?: string | null;
  };
  /** Mask tint for fallback images. Defaults to currentColor (inherits badge text). */
  logoColor?: string;
}

const BrandLogoContainer = ({ children }: { children: ReactNode }) => {
  return (
    <span className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-transparent p-1">
      {children}
    </span>
  );
};

const BrandExpertiseBadge = ({ workplace, logoColor = "currentColor" }: BrandExpertiseBadgeProps) => {
  const trimmedName = workplace.name.trim();
  const slug = workplace.slug || workplaceToBrandSlug(trimmedName);
  const label = capitalizeCarBrand(slug) || trimmedName;
  const fallbackImageUrl = formatImageUrl(workplace.imageUrl ?? workplace.image, { addAutoFormat: true });

  const renderLogo = () => {
    if (fallbackImageUrl) {
      return (
        <BrandLogoContainer>
          <ColoredLogo src={fallbackImageUrl} color={logoColor} size={24} ariaLabel={`${label} logo`} />
        </BrandLogoContainer>
      );
    }

    return (
      <span
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#345a8c]/10 text-[10px] font-semibold uppercase text-[#345a8c]"
        aria-hidden="true"
      >
        {label.charAt(0)}
      </span>
    );
  };

  return (
    <span className="inter inline-flex items-center rounded-lg border border-blue bg-blue px-2 py-1 pe-4 text-xs font-normal text-[#345a8c]">
      {renderLogo()}
      <span>{label}</span>
    </span>
  );
};

export default BrandExpertiseBadge;
